import React from 'react';
import { ChevronRight, Factory, FileText, Globe, Layers, Package, Plus } from 'lucide-react';

import {
  ActionRow, Card, EmptyState, PageHeader, PhonePage, PillButton, TabRow, TopBar,
} from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * Settings, on the admin's phone.
 *
 * The desktop is a rail of product types beside the editor for whichever one
 * is open. A phone has room for one of those at a time, so it is two screens:
 * the list of product types and the two shared settings, then the editor for
 * the one tapped, with the desktop's two tabs as a tab row and a back arrow
 * to the list. The page keeps every piece of state; this only draws it.
 */

const TABS = [
  { key: 'job_card', label: 'Job card fields', testID: 'settings-tab-job_card' },
  { key: 'stages', label: 'Stage rules', testID: 'settings-tab-stages' },
];

function Row({ icon: Icon, label, meta, onClick, testID }) {
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      className="flex min-h-[52px] w-full items-center gap-3 border-b px-1 py-2 text-left last:border-b-0"
      style={{ borderColor: Colors.border.subtle }}
    >
      <Icon className="h-5 w-5 shrink-0" style={{ color: Colors.accent.DEFAULT }} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-semibold" style={{ color: Colors.text.primary }}>{label}</span>
        {!!meta && <span className="block truncate text-[12px]" style={{ color: Colors.text.secondary }}>{meta}</span>}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0" style={{ color: Colors.text.tertiary }} />
    </button>
  );
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function PhoneAdminSettings({
  configs, selectedConfig, section, deliveryPresets, globalFields, activeTab,
  onTab, onOpenConfig, onOpenShared, onBack, onNew, onStages, onAttachments, children,
}) {
  if (section === 'product' && !selectedConfig) {
    return (
      <PhonePage testID="admin-settings-page">
        <PageHeader
          title="Settings"
          subtitle={plural(configs.length, 'product type')}
          action={(
            <PillButton onClick={onNew} testID="new-config-btn">
              <Plus className="h-4 w-4" /> New type
            </PillButton>
          )}
        />
        <div className="px-5">
          <ActionRow
            actions={[
              { key: 'stages', label: 'Production Stages', icon: Factory, onClick: onStages },
              { key: 'attachments', label: 'Attachment Categories', icon: FileText, onClick: onAttachments },
            ]}
          />
        </div>

        <div className="flex-1 px-5 pb-8">
          <Card title="Product types" className="mt-2" testID="settings-product-types">
            {configs.length === 0 && (
              <EmptyState icon={Package} message="No product types yet" hint="Add one and its job card fields and stages follow." />
            )}
            {configs.map((config) => (
              <Row
                key={config.code}
                icon={Package}
                label={config.name}
                meta={`${config.code} · ${plural(config.stage_rules_count || 0, 'stage')} · ${plural(config.job_card_fields_count || 0, 'field')}`}
                onClick={() => onOpenConfig(config)}
                testID={`config-${config.code}`}
              />
            ))}
          </Card>

          <Card title="Shared across products" className="mt-3">
            <Row
              icon={Layers}
              label="Delivery presets"
              meta={plural(deliveryPresets.length, 'preset')}
              onClick={() => onOpenShared('presets')}
              testID="open-presets"
            />
            <Row
              icon={Globe}
              label="Global field templates"
              meta={plural(globalFields.length, 'field')}
              onClick={() => onOpenShared('globals')}
              testID="open-globals"
            />
          </Card>
        </div>
      </PhonePage>
    );
  }

  const shared = section === 'presets'
    ? { title: 'Delivery presets', subtitle: 'the quick-select options on the order screen' }
    : section === 'globals'
      ? { title: 'Global field templates', subtitle: 'editing one updates every product type that uses it' }
      : null;

  return (
    <PhonePage testID="admin-settings-page">
      <TopBar
        eyebrow="SETTINGS"
        title={shared ? shared.title : selectedConfig.name}
        subtitle={shared ? shared.subtitle : selectedConfig.code}
        onBack={onBack}
      />
      {!shared && (
        <div className="px-4 pt-3">
          <TabRow tabs={TABS} active={activeTab} onSelect={onTab} />
        </div>
      )}
      <div className="flex-1 px-4 pb-10 pt-3">
        <Card>{children}</Card>
      </div>
    </PhonePage>
  );
}
